import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  RefreshCw, Search, Download, CheckCircle, XCircle, Clock, Loader2,
  DollarSign, AlertTriangle, FileText, ChevronLeft, ChevronRight,
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function AdminRefunds() {
  // V4.22 — Task 2: only the `account` role can approve/reject/process/complete
  // refunds. Super Admin and other admin-like roles can VIEW but not act.
  // Backend already enforces `current_user["role"] != "account"` → 403 (see
  // routes/refunds.py); the UI must mirror that so the buttons don't
  // mislead ops or trigger 403 toasts.
  const { user } = useAuth();
  const canAct = user?.role === 'account';
  const [data, setData] = useState({ stats: {}, refunds: [], properties: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', property_id: '', search: '', page: 1 });
  const [actionDialog, setActionDialog] = useState(null);
  const [actionForm, setActionForm] = useState({});
  const [processing, setProcessing] = useState(false);
  const [detailRefund, setDetailRefund] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.property_id) params.set('property_id', filters.property_id);
      if (filters.search) params.set('search', filters.search);
      params.set('page', filters.page);
      params.set('per_page', '20');
      const res = await api.get(`/refunds/dashboard?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load refunds'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async () => {
    if (!actionDialog) return;
    setProcessing(true);
    try {
      const { refund, action } = actionDialog;
      let url = `/refunds/${refund.id}/${action}`;
      let body = {};
      if (action === 'approve') body = { notes: actionForm.notes || '' };
      else if (action === 'reject') body = { rejection_reason: actionForm.rejection_reason || 'Rejected' };
      else if (action === 'process') body = { payment_mode: actionForm.payment_mode, bank_name: actionForm.bank_name, bank_account_number: actionForm.bank_account_number, bank_holder_name: actionForm.bank_holder_name, notes: actionForm.notes };
      else if (action === 'complete') body = { proof_file_id: actionForm.proof_file_id, notes: actionForm.notes };
      await api.put(url, body);
      toast.success(`Refund ${action}d successfully`);
      setActionDialog(null);
      setActionForm({});
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    finally { setProcessing(false); }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.property_id) params.set('property_id', filters.property_id);
      params.set('format', format);
      const res = await api.get(`/refunds/export?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `refunds.${format === 'excel' ? 'xlsx' : 'csv'}`;
      a.click();
    } catch { toast.error('Export failed'); }
  };

  const s = data.stats || {};

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-refunds">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Refund Management</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Track and process booking fee refunds</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} data-testid="export-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('excel')} data-testid="export-excel"><Download className="h-4 w-4 mr-1" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {[
          { label: 'Pending', value: s.pending || 0, color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Approved', value: s.approved || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Processing', value: s.processing || 0, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Completed', value: s.completed || 0, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Pending Amount', value: `RM ${(s.total_pending_amount || 0).toLocaleString()}`, color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Completed Amount', value: `RM ${(s.total_completed_amount || 0).toLocaleString()}`, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(st => (
          <Card key={st.label} className={st.bg}>
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-[10px] sm:text-xs text-gray-500">{st.label}</p>
              <p className={`text-base sm:text-lg font-bold ${st.color} truncate`}>{st.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 sm:items-center">
        <div className="relative flex-1 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search customer, agent, property..." className="pl-9" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} data-testid="search-input" />
        </div>
        <div className="flex gap-2">
        <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v, page: 1 }))}>
          <SelectTrigger className="flex-1 sm:flex-none sm:w-[150px]" data-testid="status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.property_id} onValueChange={v => setFilters(f => ({ ...f, property_id: v === 'all' ? '' : v, page: 1 }))}>
          <SelectTrigger className="flex-1 sm:flex-none sm:w-[200px]" data-testid="property-filter"><SelectValue placeholder="All Projects" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {(data.properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Refund List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
      ) : (data.refunds || []).length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">No refund requests found</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.refunds.map(r => (
            <Card key={r.id} className="hover:shadow-md transition" data-testid={`refund-${r.id}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={STATUS_COLORS[r.status] || 'bg-gray-100'}>{r.status}</Badge>
                      <span className="text-xs text-gray-400 font-mono">{r.id?.slice(0, 8).toUpperCase()}</span>
                    </div>
                    <p className="font-semibold text-gray-900 truncate">{r.customer_name || 'Unknown Customer'}</p>
                    <p className="text-sm text-gray-500 truncate">{r.property_name} {r.developer ? `— ${r.developer}` : ''}</p>
                    <p className="text-xs text-gray-400 mt-1">Agent: {r.agent_name || 'N/A'} | Created: {r.created_at?.slice(0, 10)}</p>
                  </div>
                  <div className="sm:text-right shrink-0">
                    <p className="text-lg font-bold text-gray-900">RM {(r.refund_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    {r.payment_mode && <p className="text-xs text-gray-500 mt-1">{r.payment_mode.replace('_', ' ')}</p>}
                    <div className="flex gap-1 mt-2 sm:justify-end flex-wrap">
                      <Button size="sm" variant="ghost" className="text-xs" onClick={() => setDetailRefund(r)} data-testid={`detail-${r.id}`}><FileText className="h-3 w-3 mr-1" />Detail</Button>
                      {canAct && r.status === 'pending' && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs" onClick={() => { setActionDialog({ refund: r, action: 'approve' }); setActionForm({}); }} data-testid={`approve-${r.id}`}><CheckCircle className="h-3 w-3 mr-1" />Approve</Button>}
                      {canAct && r.status === 'approved' && <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-xs" onClick={() => { setActionDialog({ refund: r, action: 'process' }); setActionForm({}); }} data-testid={`process-${r.id}`}><DollarSign className="h-3 w-3 mr-1" />Process</Button>}
                      {canAct && r.status === 'processing' && <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={() => { setActionDialog({ refund: r, action: 'complete' }); setActionForm({}); }} data-testid={`complete-${r.id}`}><CheckCircle className="h-3 w-3 mr-1" />Complete</Button>}
                      {canAct && ['pending', 'approved'].includes(r.status) && <Button size="sm" variant="outline" className="text-red-600 border-red-200 text-xs" onClick={() => { setActionDialog({ refund: r, action: 'reject' }); setActionForm({}); }} data-testid={`reject-${r.id}`}><XCircle className="h-3 w-3 mr-1" />Reject</Button>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {/* Pagination */}
          {data.meta?.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-gray-500 self-center">Page {filters.page} of {data.meta.total_pages}</span>
              <Button variant="outline" size="sm" disabled={filters.page >= data.meta.total_pages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.action === 'approve' && 'Approve Refund'}
              {actionDialog?.action === 'reject' && 'Reject Refund'}
              {actionDialog?.action === 'process' && 'Process Refund Payment'}
              {actionDialog?.action === 'complete' && 'Complete Refund'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{actionDialog?.refund?.customer_name} — RM {(actionDialog?.refund?.refund_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            {actionDialog?.action === 'reject' && (
              <div><Label>Rejection Reason *</Label><Textarea value={actionForm.rejection_reason || ''} onChange={e => setActionForm(f => ({ ...f, rejection_reason: e.target.value }))} placeholder="Reason for rejection" data-testid="rejection-reason" /></div>
            )}
            {actionDialog?.action === 'process' && (
              <>
                <div><Label>Payment Mode *</Label>
                  <Select value={actionForm.payment_mode || ''} onValueChange={v => setActionForm(f => ({ ...f, payment_mode: v }))}>
                    <SelectTrigger data-testid="payment-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Bank Name</Label><Input value={actionForm.bank_name || ''} onChange={e => setActionForm(f => ({ ...f, bank_name: e.target.value }))} data-testid="bank-name" /></div>
                <div><Label>Account Number</Label><Input value={actionForm.bank_account_number || ''} onChange={e => setActionForm(f => ({ ...f, bank_account_number: e.target.value }))} data-testid="account-number" /></div>
                <div><Label>Holder Name</Label><Input value={actionForm.bank_holder_name || ''} onChange={e => setActionForm(f => ({ ...f, bank_holder_name: e.target.value }))} data-testid="holder-name" /></div>
              </>
            )}
            {['approve', 'process', 'complete'].includes(actionDialog?.action) && (
              <div><Label>Notes</Label><Textarea value={actionForm.notes || ''} onChange={e => setActionForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" data-testid="action-notes" /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={handleAction} disabled={processing || (actionDialog?.action === 'reject' && !actionForm.rejection_reason) || (actionDialog?.action === 'process' && !actionForm.payment_mode)} data-testid="confirm-action">
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailRefund} onOpenChange={() => setDetailRefund(null)}>
        <DialogContent className="max-w-lg" data-testid="detail-dialog">
          <DialogHeader><DialogTitle>Refund Detail</DialogTitle></DialogHeader>
          {detailRefund && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">ID</span><span className="font-mono">{detailRefund.id?.slice(0, 8).toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><Badge className={STATUS_COLORS[detailRefund.status]}>{detailRefund.status}</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-semibold">RM {(detailRefund.refund_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Customer</span><span>{detailRefund.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Agent</span><span>{detailRefund.agent_name || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Property</span><span>{detailRefund.property_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Reason</span><span>{detailRefund.reason}</span></div>
              {detailRefund.payment_mode && <div className="flex justify-between"><span className="text-gray-500">Payment Mode</span><span>{detailRefund.payment_mode.replace('_', ' ')}</span></div>}
              {detailRefund.rejection_reason && <div className="flex justify-between"><span className="text-gray-500">Rejection</span><span className="text-red-600">{detailRefund.rejection_reason}</span></div>}
              <div className="flex justify-between"><span className="text-gray-500">Created</span><span>{detailRefund.created_at?.slice(0, 16).replace('T', ' ')}</span></div>
              {detailRefund.status_history?.length > 0 && (
                <div>
                  <p className="text-gray-500 font-medium mb-2">Audit Trail</p>
                  <div className="space-y-1 border-l-2 border-gray-200 pl-3">
                    {detailRefund.status_history.map((h, i) => (
                      <div key={i} className="text-xs">
                        <Badge variant="outline" className="text-[10px] mr-1">{h.status}</Badge>
                        <span className="text-gray-400">{h.changed_at?.slice(0, 16).replace('T', ' ')}</span>
                        {h.notes && <span className="text-gray-500 ml-1">— {h.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
